import { QueryKey, keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Environment, SortDirection, TimePrecision } from '@playq/octopus-common';
import { BookkeeperId, PaymentsSort, PaymentsSortField } from '@playq/octopus2-tgservices';
import { UserPurchasesLookup } from '@playq/services-bookkeeper';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { services2 } from '/api/services2';
import { toMs } from '/helpers/toMs';

const paymentKeys: QueryKey = ['payments', 'bookkeeper'];
export const iniSort = new PaymentsSort({ field: PaymentsSortField.Date, ord: SortDirection.Descending });

export const useGetPayments = (
  id: BookkeeperId,
  env: Environment,
  iterator: OffsetLimit,
  sortBy: PaymentsSort = iniSort,
  options?: IUseQueryOptionsExtended<UserPurchasesLookup | undefined, GenericFailure | Error>
) => {
  const keys: QueryKey = useMemo(() => paymentKeys.concat(id, env, iterator, sortBy), [env, id, iterator, sortBy]);
  const { mutate, ...res } = useEitherQuery(
    keys,
    () => services2.bookkeeperLookupService.getPayments(id, env, iterator, sortBy),
    {
      ...options,
      placeholderData: keepPreviousData,
      staleTime: toMs(TimePrecision.Minute, 3),
    }
  );

  useQueryPrefetch({
    keys,
    total: res.data?.paymentsCount ?? 0,
    args: [id, env, iterator, sortBy],
    serviceMethod: services2.bookkeeperLookupService.getPayments.bind(services2.bookkeeperLookupService),
  });
  return {
    ...res,
    payments: res.data?.payments ?? [],
    total: res.data?.paymentsCount ?? 0,
    queryKeys: keys,
    mutate,
  };
};
